// fork(oem): icon imports trimmed with the GROUPS list below
import {
  Bell,
  Factory,
  Palette,
  ShieldCheck,
  UserRound,
  type LucideIcon,
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { Commentable } from '@/shared/comments/Commentable';
import { useRole } from '@/versions/v1/shared/RoleContext';
import {
  SidebarRail,
  SidebarRailButton,
  SidebarRow,
  SidebarSection,
  SidebarShell,
} from './SidebarShell';
import { SidebarSceneHeader } from './SidebarSceneHeader';

type Section = {
  href: string;
  label: string;
  Icon: LucideIcon;
};

type SectionGroup = {
  label: string;
  items: Section[];
};

// fork(oem): trimmed to Account / Appearance / OEM identity / Notifications.
// "OEM identity" is a deep link into the Appearance page's brand cards
// (Meridian default, Komatsu opt-in) so the switch is one click from Settings.
// The Privacy,
// Permissions, Linked accounts, Install, Tools and Agent surfaces are
// foundation scaffold that OEM+ does not demo — their scenes and routes are
// left intact (scripts/parity/snapshot-targets.mjs renders several of them),
// they are only dropped from the nav. Restore a row to bring one back.
const GROUPS: SectionGroup[] = [
  {
    label: 'Personal',
    items: [
      { href: '/settings/account', label: 'Account', Icon: UserRound },
      { href: '/settings/appearance', label: 'Appearance', Icon: Palette },
      { href: '/settings/appearance#oem-identity', label: 'OEM identity', Icon: Factory },
      { href: '/settings/notifications', label: 'Notifications', Icon: Bell },
    ],
  },
];

// fork(oem): hash-aware, so "OEM identity" (a #fragment on the Appearance
// page) and "Appearance" are never both highlighted.
function isActive(pathname: string, href: string, hash = ''): boolean {
  const [path, fragment = ''] = href.split('#');
  if (fragment) return pathname === path && hash === `#${fragment}`;
  if (pathname === path) return hash !== '#oem-identity';
  return pathname.startsWith(`${path}/`);
}

export function SettingsSidebar() {
  const { pathname, hash } = useLocation();
  const { can } = useRole();
  const isAdmin = can('roles:admin');
  const railItems = GROUPS.flatMap((group) => group.items);

  return (
    <Commentable name="SettingsSidebar">
      <SidebarShell
        ariaLabel="Settings navigation"
        collapsedRail={
          <SidebarRail>
            {railItems.map((item) => (
              <SidebarRailButton
                key={item.href}
                href={item.href}
                icon={item.Icon}
                label={item.label}
                isActive={isActive(pathname, item.href, hash)}
              />
            ))}
            {isAdmin ? (
              <SidebarRailButton
                href="/admin"
                icon={ShieldCheck}
                label="Admin panel"
                isActive={isActive(pathname, '/admin')}
              />
            ) : null}
          </SidebarRail>
        }
        header={
          <SidebarSceneHeader
            title="Settings"
            back={{ href: '/', label: 'Back to chats' }}
          />
        }
      >
        <nav aria-label="Settings sections">
          {GROUPS.map((group, gi) => (
            <SidebarSection key={group.label} label={group.label} first={gi === 0}>
              <ul className="space-y-0">
                {group.items.map((item) => (
                  <li key={item.href}>
                    <SidebarRow
                      href={item.href}
                      icon={item.Icon}
                      label={item.label}
                      isActive={isActive(pathname, item.href, hash)}
                    />
                  </li>
                ))}
              </ul>
            </SidebarSection>
          ))}
          {isAdmin ? (
            <SidebarSection label="Admin">
              <ul className="space-y-0">
                <li>
                  <SidebarRow
                    href="/admin"
                    icon={ShieldCheck}
                    label="Admin panel"
                    isActive={isActive(pathname, '/admin')}
                  />
                </li>
              </ul>
            </SidebarSection>
          ) : null}
        </nav>
      </SidebarShell>
    </Commentable>
  );
}
